// The ledger's column capability, as each resource declares it.
//
// `OPTIONS /api/<resource>` is the authoritative statement of what a ledger
// resource accepts: which properties exist, their neutral types, the operators
// each takes, whether it may order them, and its static enum members (D6/D7).
// The presentation registry (`columns.ts`) carries what OPTIONS does not — the
// label, the responsive priority, the width, the alignment, the cell's relation
// or zone link, the value→label map, the slider's domain — so the two are folded
// together here rather than duplicated: the capability comes from the server and
// the presentation stays the client's.
//
// A column the server does not declare keeps its own declaration rather than
// vanishing: a presentation-only column (a correlated count, a link cell) is
// still a column of the grid, and dropping it would take the row's reading away
// with it. A resource whose document never arrived (or does not answer) keeps the
// whole static registry, so a missing capability degrades to what the workbench
// shipped rather than to a columnless grid.

import type {
	MapReviewCapability,
	MapReviewCapabilityProperty,
	MapReviewFilterOp,
	MapReviewTable,
} from "@/type/api";

import {
	MAP_REVIEW_COLUMNS,
	type MapReviewColumn,
	type MapReviewColumnOption,
} from "./columns";

/**
 * The grid's filter controls, one per operator family the server declares over a
 * property. `empty`/`not-empty` have no control of their own — the filter row
 * cannot state "has no value" — and neither does `not-contains`, so a property
 * offering only those reads as display-only here, exactly as it renders.
 */
function filterOpsFor(
	property: MapReviewCapabilityProperty,
): MapReviewFilterOp[] {
	const ops = property.operators;
	const controls: MapReviewFilterOp[] = [];
	if (ops.includes("contains")) controls.push("contains");
	if (ops.includes("equal")) controls.push("enum");
	if (
		ops.some((op) => op === "lt" || op === "lte" || op === "gt" || op === "gte")
	) {
		controls.push("numberRange");
	}
	if (ops.some((op) => op === "before" || op === "after")) {
		controls.push("dateRange");
	}
	return controls;
}

/**
 * An enum property's static members, as the filter panel's options. The server
 * declares the vocabulary and the registry labels it: a member the registry
 * already names keeps its label, and one it does not is offered under its own
 * wire value, so a vocabulary the server has grown is still selectable rather
 * than silently missing from the panel.
 */
function optionsFrom(
	values: readonly string[],
	column: MapReviewColumn,
): MapReviewColumnOption[] {
	const labels = new Map(
		(column.options ?? []).map((option) => [
			String(option.value),
			option.label,
		]),
	);
	return values.map((value) => ({ value, label: labels.get(value) ?? value }));
}

/**
 * One table's columns, with the capability the server declares for them.
 *
 * `doc` absent (the resource has not answered OPTIONS, or does not serve one)
 * leaves the registry's own declaration in place, which is what the workbench
 * rendered before this existed.
 */
export function mergedColumns(
	table: MapReviewTable,
	doc: MapReviewCapability | undefined,
): MapReviewColumn[] {
	const registry = MAP_REVIEW_COLUMNS[table];
	if (!doc) return registry;
	const declared = new Map<string, MapReviewCapabilityProperty>(
		doc.properties.map((property) => [property.name, property]),
	);
	return registry.map((column) => {
		const property = declared.get(column.key);
		if (!property) return column;
		return {
			...column,
			// Sortability and the filter controls are the server's answer: a
			// clause it would refuse 422 must not be offered by the row, and an
			// ordering it would refuse must not be sent.
			sortable: property.sortable,
			filterOps: filterOpsFor(property),
			// A static vocabulary is the server's; an id-backed or runtime one
			// declares no members (D33) and keeps the registry's store-backed
			// options. `valueLabels` stays the registry's: it labels the CELL, and
			// a member the registry does not label still reads as its wire value.
			options: property.values?.length
				? optionsFrom(property.values, column)
				: column.options,
		};
	});
}
